<template>
  <FullScreenModal container-class="w-full" :close-button="false">
    <div class="h-full flex flex-col">
      <div class="border-b border-solid border-gray-200 shadow-sm py-2 min-h-14 flex items-center px-6 justify-between flex-shrink-0">
        <div class="text-18 font-700">Agent Editor</div>
        <div class="flex items-center gap-x-3">
          <span v-if="agentName" class="text-14 text-gray-500 font-500">{{ agentName }}</span>
          <PillButton v-if="isDirty" text="Save" primary :loading="saving" :disabled="saving" @click="onSave" />
          <PillButton text="Close" outlined @click="onClose" />
        </div>
      </div>

      <div v-if="loading" class="flex-grow flex items-center justify-center text-gray-500 text-14">
        <Icon name="loading_dots" class="w-14 text-primary" />
      </div>

      <div v-else-if="agentItem" class="flex-grow overflow-hidden">
        <AgentEvaluateEditor :agent-item="agentItem" @update="onAgentUpdated" />
      </div>

      <div v-else class="flex-grow flex items-center justify-center text-gray-400 text-13">Failed to load agent configuration.</div>
    </div>
  </FullScreenModal>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex';
import AgentEvaluateEditor from './AgentEvaluateEditor.vue';

export default {
  name: 'AgentEditorModal',
  components: { AgentEvaluateEditor },
  props: {
    flowId: { type: String, required: true },
    projectId: { type: String, required: true },
  },
  data() {
    return {
      loading: true,
      saving: false,
      isDirty: false,
      agentItem: null,
      agentName: '',
    };
  },
  computed: {
    ...mapState(['selectedIntentDefinition']),
  },
  methods: {
    ...mapActions(['fetchProjectFlow', 'updateProjectFlow', 'showToastMessage']),
    ...mapMutations(['SET_SELECTED_INTENT_DEFINITION']),
    onAgentUpdated(updatedAgentItem) {
      // The config forms only emit on real user interaction (their mount-time
      // watch merges defaults without emitting), so any update is a genuine change.
      this.agentItem = updatedAgentItem;
      this.isDirty = true;
    },
    async onSave() {
      this.saving = true;
      const response = await this.updateProjectFlow({
        project_id: this.projectId,
        flow_id: this.flowId,
        name: this.agentName,
        flowType: 'Agent',
        agent: this.agentItem,
        steps: [],
        transition: '',
      });
      if (response) {
        this.isDirty = false;
        this.showToastMessage({ message: 'Agent saved successfully', type: 'success' });
        // `close` listeners are swallowed by $showModal's own onClose, so signal
        // a successful save via a distinct event for callers to refresh on.
        this.$emit('saved');
      }
      this.saving = false;
    },
    onClose() {
      if (this.isDirty) {
        // eslint-disable-next-line
        const confirmed = window.confirm('You have unsaved changes. Are you sure you want to close?');
        if (!confirmed) return;
      }
      this.SET_SELECTED_INTENT_DEFINITION(null);
      this.$emit('close');
    },
    loadFromDefinition() {
      const def = this.selectedIntentDefinition;
      if (def?.agent) {
        this.agentItem = JSON.parse(JSON.stringify(def.agent));
        this.agentName = def.name || '';
      }
    },
  },
  async mounted() {
    this.loading = true;
    try {
      await this.fetchProjectFlow({ projectId: this.projectId, flowId: this.flowId });
      this.loadFromDefinition();
    } catch (e) {
      this.showToastMessage({ title: 'Failed to load agent', type: 'error' });
    }
    this.loading = false;
  },
  beforeUnmount() {
    this.SET_SELECTED_INTENT_DEFINITION(null);
  },
};
</script>
